import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTenantInfo } from '../resources/api.resource';
import type { TenantInfo } from '../resources/api.resource';

const setFavicon = (href?: string | null) => {
  if (!href) return;
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
};

interface TenantInfoCtx {
  tenantInfo: TenantInfo | undefined;
  isLoading: boolean;
  error: Error | null;
}

const TenantInfoContext = createContext<TenantInfoCtx>({
  tenantInfo: undefined,
  isLoading: false,
  error: null,
});

export const TenantInfoProvider = ({ children }: { children: ReactNode }) => {
  const { data, isLoading, error } = useQuery<TenantInfo, Error>({
    queryKey: ['tenant', 'me'],
    queryFn: getTenantInfo,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => { setFavicon(data?.faviconUrl); }, [data?.faviconUrl]);

  return (
    <TenantInfoContext.Provider value={{ tenantInfo: data, isLoading, error: error ?? null }}>
      {children}
    </TenantInfoContext.Provider>
  );
};

export const useTenantInfo = () => useContext(TenantInfoContext);
